import {Injectable} from '@angular/core';
import {
  CreateDisclosureTemplateHostDirective
} from './create-disclosure/shared/create-disclosure-template-viewport/create-disclosure-template-host.directive';
import {StatementTrackerService} from './statement-tracker.service';
import {DSTFormState} from '../shared/models/dst-form-state.model';

@Injectable({
  providedIn: 'root'
})
export class TrackerTabsService {

  private _trackerTabs: TrackerTabState[] = [];
  private _createNewTab!: TrackerTabState | null;
  private _modalTab!: TrackerTabState | null; // modal tab is used in a modal container
  private _activeId!: string; // activeId = 'Statement Tracker' means no active tab, so show tracker

  constructor() {
  }

  set activeId(activeId: string) {
    this._activeId = activeId;
  }

  get trackerTabs(): TrackerTabState[] {
    return this._trackerTabs;
  }

  createTrackerTab(newTabState: TrackerTabState): void {
    if (newTabState.id === StatementTrackerService.ID_MODAL) {
      this._modalTab = newTabState;
    } else if (newTabState.id === StatementTrackerService.ID_CREATE_NEW) {
      this._createNewTab = newTabState;
    } else {
      this._trackerTabs.push(newTabState);
    }
  }

  destroyModalTab(): void {
    this._modalTab = null;
  }

  removeTrackerTabById(id: string): void {
    let index: number = -1;
    if (id === StatementTrackerService.ID_CREATE_NEW) {
      this._createNewTab = null;
    } else {
      index = this._trackerTabs.findIndex(t => t.id === id);
      if (index >= 0) {
        this._trackerTabs.splice(index, 1);
      }
    }

    if (id === this._activeId) {
      this.activatePrev(index);
    }
  }

  private activatePrev(deletedIndex: number): void {
    // activate previous tab
    if (deletedIndex > 0) {
      this._activeId = this._trackerTabs[deletedIndex - 1].id;
    } else if (this._createNewTab) {
      this._activeId = StatementTrackerService.ID_CREATE_NEW;
    } else {
      this._activeId = StatementTrackerService.ID_TRACKER;
    }
  }

  getActiveTrackerTab(): TrackerTabState | null {
    if (this._modalTab) {
      return this._modalTab;
    }
    if (this._activeId === StatementTrackerService.ID_CREATE_NEW) {
      return this._createNewTab;
    }
    return this._trackerTabs.find(tab => tab.id === this._activeId) || null;
  }

  findTrackerTabById(id: string): TrackerTabState | null {
    if (id === StatementTrackerService.ID_CREATE_NEW) {
      return this._createNewTab;
    }
    return this._trackerTabs.find(tab => tab.id === id) || null;
  }

  setActiveTrackerTabChildIndex(index: number): void {
    const activeTrackerTab = this.getActiveTrackerTab();
    activeTrackerTab?.setActiveChildIndex(index);
  }

  getActiveTrackerTabChildIndex(): number | null {
    const activeTrackerTab = this.getActiveTrackerTab();
    return activeTrackerTab?.childIndex || null;
  }

}

export class TrackerTabState {
  private readonly _id: string; // statementId or csStatementId or "Create New" or "Modal"
  private _activeTabIndex: number; // active form template tab index
  private _formStateArray!: DSTFormState[]; // each tracker tab can have multiple form template tabs for associated statements support
  private _templateHostArray!: CreateDisclosureTemplateHostDirective[]; // each tracker tab can have multiple form template tabs for associated statements support
  private _isPlaceholderChecked: boolean;
  private _parentTransDate: string; // parent transaction date is used for auto-generated disclosures' name change query
  private _childIndex: number; // child index is used to store child disclosure index for redirection

  constructor(id: string) {
    this._id = id;
    this._activeTabIndex = 0;
    this._childIndex = 0;
    this._formStateArray = [];
    this._templateHostArray = [];
    this._isPlaceholderChecked = false;
    this._parentTransDate = '';
  }

  get id(): string {
    return this._id;
  }

  get formStateArray(): DSTFormState[] {
    return this._formStateArray;
  }

  get templateHostArray(): CreateDisclosureTemplateHostDirective[] {
    return this._templateHostArray;
  }

  get activeTabIndex(): number {
    return this._activeTabIndex;
  }

  get childIndex(): number {
    return this._childIndex;
  }

  get isPlaceholderChecked(): boolean {
    return this._isPlaceholderChecked;
  }

  get parentTransDate(): string {
    return this._parentTransDate;
  }

  set activeTabIndex(value: number) {
    this._activeTabIndex = value;
  }

  set childIndex(value: number) {
    this._childIndex = value;
  }

  set isPlaceholderChecked(value: boolean) {
    this._isPlaceholderChecked = value;
  }

  set parentTransDate(value: string) {
    this._parentTransDate = value;
  }

  addFormState(taxYear: number, taxYearId: number, returnPeriodId: number, statementId: string, formId: number, formName: string,
                     formType: string, formTypeId: number, cseId: number, isChild: boolean, isView?: boolean, reset?: boolean, uploadType?: string, csStatementId?: number, is304?: boolean): void {
    if (reset) {
      this._formStateArray = [];
      this._templateHostArray = [];
    }
    const formState: DSTFormState = new DSTFormState(taxYear, taxYearId, returnPeriodId, statementId, formId, formName, formType, formTypeId, cseId, isChild, isView, uploadType, csStatementId, is304);
    this._formStateArray.push(formState);
  }

  setTemplateHost(host: CreateDisclosureTemplateHostDirective, index: number): void {
    if (index < 0 || index > this._templateHostArray.length) {
      throw Error('Template Host index out of range!');
    }
    this._templateHostArray[index] = host;
  }

  getActiveFormState(): DSTFormState {
    return this._formStateArray[this._activeTabIndex];
  }

  getActiveTemplateHost(): CreateDisclosureTemplateHostDirective {
    return this._templateHostArray[this._activeTabIndex];
  }

  getFormStateByFormId(formId: number): DSTFormState | undefined {
    return this._formStateArray.find((f: DSTFormState) => Number(f.formId) === Number(formId));
  }

  setActiveChildIndex(index: number): void {
    this._childIndex = index;
  }
}
